// src/components/stages/QueueStage.tsx
import { useState, useEffect } from 'react';
import { addToQueue, updateQueueAssignment, getUsersByRole, getDoctorOnDutyStatus, queueStatusUrl,
  getDoctorConsultationState, startEmergencyConsultation } from '../../api';
import type { StageProps } from '../../types/visit';
import { fetchQueuePriorityRules, fetchTodayQueue } from '@/features/queue/api';
import { OutOfOrderDialog, type ExpectedNext } from '@/features/queue/OutOfOrderDialog';
import type { QueuePriorityRules } from '@/features/admin/queue-priority/types';
import { StageLayout } from '../ui/StageLayout';
import { StageActionBar } from '../ui/StageActionBar';

function fmtTime(t: string | undefined | null): string { return (t || '').slice(0, 5); }

type OnDutyStatusCode = 'on_duty' | 'on_break' | 'outside_hours' | 'not_scheduled' | 'unavailable_exception';

interface DoctorStatus {
  status: OnDutyStatusCode;
  waiting_count?: number;
  next_eta_minutes?: number;
}

const STATUS_META: Record<OnDutyStatusCode, { emoji: string; label: string }> = {
  on_duty:               { emoji: '🟢', label: 'On duty' },
  on_break:              { emoji: '🟡', label: 'On break' },
  outside_hours:         { emoji: '🟠', label: 'Outside hours' },
  not_scheduled:         { emoji: '⚪', label: 'Not scheduled today' },
  unavailable_exception: { emoji: '🔴', label: 'Reported unavailable' },
};
function doctorStatusLine(status: DoctorStatus | undefined): string {
  if (!status) return '';
  const meta = STATUS_META[status.status] || ({} as { emoji?: string; label?: string });
  const load = status.waiting_count
    ? ` · ${status.waiting_count} waiting, ~${status.next_eta_minutes} min`
    : status.next_eta_minutes ? ` · ~${status.next_eta_minutes} min` : ' · free now';
  return `${meta.emoji || ''} ${meta.label || status.status}${load}`;
}

interface DoctorOption {
  id: number;
  full_name: string;
}

type Priority = 'Normal' | 'Urgent' | 'Emergency';

/** The consultation a doctor is currently in — see publicRow() in
 *  backend/utils/consultationState.js. Narrowed to what this screen shows. */
interface ActiveConsultation {
  queue_id: number;
  patient_name: string;
  display_token: string | null;
  priority: Priority;
  consultation_started_at: string | null;
}

interface DoctorConsultationState {
  busy: boolean;
  active: ActiveConsultation | null;
  held: ActiveConsultation[];
}

/** The queue row this stage reads/writes — a superset of StageProps'
 * queueEntry (which is deliberately loose since several call sites pass a
 * freshly-fetched row through it), narrowed to what's actually used here. */
interface QueueRow {
  id: number;
  doctor_id?: number | null;
  display_token?: string | number | null;
  status_token?: string | null;
  appointment_slot_id?: number | null;
  priority?: Priority;
  is_referral?: boolean;
  referral_source?: string | null;
  travel_distance_km?: number | null;
  /** Keys of the admin-defined custom priorities ticked for this patient. */
  priority_tags?: string[];
}

export default function QueueStage({ visit, completeStage, loading, todayAppointment, queueEntry }: Pick<StageProps, 'visit' | 'completeStage' | 'loading' | 'todayAppointment' | 'queueEntry'>) {
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [onDutyStatus, setOnDutyStatus] = useState<Record<number, DoctorStatus>>({});
  const [doctorQuery, setDoctorQuery] = useState('');
  const [doctor, setDoctor] = useState<DoctorOption | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [priority, setPriority] = useState<Priority>('Normal');
  // The patient's queue entry — normally already created at check-in (see
  // VisitWorkflow.jsx's checkInToQueue) and passed in via the queueEntry
  // prop. A booked appointment's token is minted right there (the doctor is
  // already known from the slot); a walk-in's token stays null until a
  // doctor is picked here (routes/queue.js's PATCH /:id mints it then,
  // since only now is the doctor — and therefore the per-doctor token
  // series — actually known). Falls back to creating a fresh row on the
  // spot if check-in didn't happen for some reason (e.g. a network hiccup),
  // so the visit is never blocked on this.
  const [localQueueItem, setLocalQueueItem] = useState<QueueRow | null>((queueEntry as QueueRow | null) || null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Set when the assigned doctor is already with someone else — this patient
  // has NOT been moved and stays Ready for Consultation.
  const [busyWith, setBusyWith] = useState<ActiveConsultation | null>(null);
  const [confirmEmergency, setConfirmEmergency] = useState(false);
  // Queue Prioritization call order: this patient is not next in line, so
  // Continue asks for a reason first (the server enforces the same rule on
  // the stage advance and is the source of truth).
  const [skip, setSkip] = useState<{ expected: ExpectedNext; position: number | null; stageData: Record<string, unknown> } | null>(null);

  // Queue Prioritization (Admin → Facility). The referral / travel fields
  // appear only when this branch has that preference switched on, and the
  // priority + preference controls are read-only for a role without
  // queue:priority. The server enforces both; this only shows the right
  // controls. A failed read hides the preference fields and leaves priority
  // editable — the server still refuses a change the role may not make.
  const [rules, setRules] = useState<QueuePriorityRules | null>(null);
  const [isReferral, setIsReferral] = useState(false);
  const [referralSource, setReferralSource] = useState('');
  const [travelKm, setTravelKm] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => { fetchQueuePriorityRules().then(setRules).catch(() => setRules(null)); }, []);
  // Only MANUAL custom priorities get a tick box; automatic ones are decided by
  // the server from the patient record and are listed for information only.
  const customOn = rules ? rules.rules.custom.filter((c) => c.enabled && c.assignment !== 'automatic') : [];
  const customAuto = rules ? rules.rules.custom.filter((c) => c.enabled && c.assignment === 'automatic') : [];
  // Long-distance travel is either typed in here (manual) or worked out by the
  // server from the patient's address (automatic) — then there is no km field.
  const travelManual = !!rules && rules.rules.travel.enabled && rules.rules.travel.assignment !== 'automatic';
  const autoLabels = rules ? [
    ...(rules.rules.travel.enabled && rules.rules.travel.assignment === 'automatic' ? ['Long-distance travellers (from address)'] : []),
    ...customAuto.map((c) => c.label),
  ] : [];
  const canChangePriority = rules?.can_change_priority !== false;
  const travelValue = travelKm.trim() === '' ? null : Number(travelKm);
  const travelInvalid = travelValue !== null && (!Number.isInteger(travelValue) || travelValue < 0 || travelValue > 5000);

  useEffect(() => {
    if (queueEntry) {
      const q = queueEntry as QueueRow;
      setLocalQueueItem(q);
      setPriority(q.priority || 'Normal');
      setIsReferral(!!q.is_referral);
      setReferralSource(q.referral_source || '');
      setTravelKm(q.travel_distance_km != null ? String(q.travel_distance_km) : '');
      setTags(Array.isArray(q.priority_tags) ? q.priority_tags : []);
    }
  }, [queueEntry]);

  // Sent only for a preference that is switched on, and only by a role that
  // may change it — otherwise the stored values are left exactly as they are.
  const preferencePayload = (): Record<string, unknown> => {
    if (!rules || !canChangePriority) return {};
    return {
      ...(rules.rules.referral.enabled ? { is_referral: isReferral, referral_source: isReferral ? referralSource.trim() || null : null } : {}),
      ...(travelManual && !travelInvalid ? { travel_distance_km: travelValue } : {}),
      // Only keys the branch still defines (the server refuses unknown ones);
      // a tag for a category that is currently switched off is kept as-is.
      ...(rules.rules.custom.length
        ? { priority_tags: tags.filter((t) => rules.rules.custom.some((c) => c.key === t && c.assignment !== 'automatic')) }
        : {}),
    };
  };
  const toggleTag = (key: string, on: boolean) =>
    setTags((prev) => (on ? [...new Set([...prev, key])] : prev.filter((t) => t !== key)));
  useEffect(() => { getUsersByRole('Doctor').then(setDoctors).catch(() => setDoctors([])); }, []);
  // Informational only (see routes/doctor-availability.js's on-duty-status
  // endpoint) — never blocks picking a doctor, just helps staff choose one
  // who's actually working and not already badly backed up.
  useEffect(() => {
    getDoctorOnDutyStatus()
      .then((rows: Array<DoctorStatus & { id: number }>) => setOnDutyStatus(Object.fromEntries(rows.map(r => [r.id, r]))))
      .catch(() => setOnDutyStatus({}));
  }, []);

  // Prefer the doctor already assigned on the queue entry (the real,
  // persisted assignment); fall back to the booked appointment's doctor if
  // check-in somehow didn't resolve one yet. Staff can still pick "Change".
  useEffect(() => {
    if (!doctors.length || doctor) return;
    const assignedId = localQueueItem?.doctor_id || todayAppointment?.doctor_id;
    if (assignedId) {
      const match = doctors.find(d => d.id === assignedId);
      if (match) setDoctor(match);
    }
  }, [localQueueItem, todayAppointment, doctors]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = doctors.filter(d => d.full_name.toLowerCase().includes(doctorQuery.toLowerCase()));

  // Picking a doctor for a token-less entry (a walk-in) mints its token
  // immediately, rather than only on "Continue" — so staff can actually see
  // and relay the number to the patient before this screen navigates away.
  const handleSelectDoctor = async (d: DoctorOption) => {
    setDoctor(d);
    setPickerOpen(false);
    if (localQueueItem && !localQueueItem.display_token) {
      setError('');
      try {
        const updated = await updateQueueAssignment(localQueueItem.id, { doctor_id: d.id, priority, ...preferencePayload() });
        setLocalQueueItem(updated);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    }
  };

  // Fallback path only — normally there's already a queueEntry by the time
  // staff reach this screen.
  const handleAdd = async () => {
    setSaving(true); setError('');
    try {
      const item = await addToQueue(visit.patient_id!, {
        doctor_id: doctor?.id || null,
        priority,
        appointment_slot_id: todayAppointment?.id || null,
        ...preferencePayload(),
      });
      setLocalQueueItem(item);
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  // A doctor may have only one active consultation at a time (see
  // backend/utils/consultationState.js). Completing this stage is what opens
  // the Consultation screen, so it is also where that rule bites — the
  // backend enforces it on PATCH /visits/:id/stage and is the source of
  // truth. This is a *pre-flight* read only: it lets the screen say who the
  // doctor is with, and offer the emergency interruption, before navigating,
  // instead of bouncing off a 409 after the consultation page has opened.
  const handleContinue = async () => {
    if (!localQueueItem) return;
    if (travelInvalid) { setError('Travel distance must be a whole number of km (0–5000).'); return; }
    setSaving(true); setError(''); setBusyWith(null);
    try {
      const updated = await updateQueueAssignment(localQueueItem.id, { doctor_id: doctor?.id || null, priority, ...preferencePayload() });
      if (doctor?.id) {
        const state: DoctorConsultationState = await getDoctorConsultationState(doctor.id);
        const active = state.active;
        if (active && active.queue_id !== updated.id) {
          // Nothing is written and nothing moves: this patient stays Ready
          // for Consultation, exactly as the backend would leave them.
          setBusyWith(active);
          setSaving(false);
          return;
        }
      }
      const stageData = { queue_id: updated.id, token: updated.display_token, doctor_id: doctor?.id || null, doctor: doctor?.full_name || '', priority };
      if (doctor?.id && priority !== 'Emergency' && rules?.rules.call_order !== 'off') {
        const ahead = await nextInLineFor(doctor.id, updated.id);
        if (ahead) {
          setSkip({ ...ahead, stageData });
          setSaving(false);
          return;
        }
      }
      completeStage('queue', stageData);
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };

  // Who the server's queue order says this doctor sees next, if not this
  // patient. Advisory pre-flight only; a failed read lets Continue go ahead
  // and the server decides.
  const nextInLineFor = async (doctorId: number, queueId: number): Promise<{ expected: ExpectedNext; position: number | null } | null> => {
    try {
      const rows = (await fetchTodayQueue())
        .filter((r) => r.doctor_id === doctorId && r.queue_position != null && r.status !== 'In Consultation' && r.status !== 'Completed')
        .sort((a, b) => (a.queue_position ?? 0) - (b.queue_position ?? 0));
      const next = rows[0];
      if (!next || next.id === queueId) return null;
      const idx = rows.findIndex((r) => r.id === queueId);
      return { expected: { patient_name: next.name, display_token: next.display_token }, position: idx >= 0 ? idx + 1 : null };
    } catch { return null; }
  };

  // §3: the emergency interruption. Holds the doctor's current patient and
  // starts this one, in one server transaction, then advances the stage.
  const handleAttendEmergency = async () => {
    if (!localQueueItem) return;
    setSaving(true); setError('');
    try {
      const updated = await updateQueueAssignment(localQueueItem.id, { doctor_id: doctor?.id || null, priority: 'Emergency' });
      await startEmergencyConsultation(updated.id);
      setBusyWith(null); setConfirmEmergency(false);
      completeStage('queue', { queue_id: updated.id, token: updated.display_token, doctor_id: doctor?.id || null, doctor: doctor?.full_name || '', priority: 'Emergency' });
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };

  const hasToken = !!localQueueItem?.display_token;

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    <div className="card">
      <div style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>🎫 Queue assignment</div>
      <div style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>
        {hasToken ? 'Confirm doctor and priority for this patient\'s queue token'
          : localQueueItem ? 'Pick a doctor to generate this patient\'s queue token'
          : 'Assign patient to doctor queue'}
      </div>

      {todayAppointment && (
        <div style={{ padding:'10px 14px', background:'#ECFDF5', border:'1px solid #A7F3D0', borderRadius:'var(--radius)', color:'#065F46', fontSize:13, marginBottom:16 }}>
          📅 Booked appointment: {fmtTime(todayAppointment.start_time)} with {todayAppointment.doctor_name || ''}
        </div>
      )}

      {error && <div style={{ padding:'10px 14px', background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:'var(--radius)', color:'#991B1B', fontSize:13, marginBottom:16 }}>{error}</div>}

      {/* §11: a doctor who is already attending someone. The patient stays
          Ready for Consultation and the stepper does not advance — said
          plainly, with the one legitimate way past it (an emergency
          interruption) offered as an explicit, confirmed action. */}
      {busyWith && (
        <div style={{ padding:'12px 14px', background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:'var(--radius)', color:'#92400E', fontSize:13, marginBottom:16 }}>
          <div style={{ fontWeight:700, marginBottom:4 }}>⚠ Doctor busy</div>
          <div>
            {doctor?.full_name || 'This doctor'} is currently attending <b>{busyWith.patient_name}</b>
            {busyWith.display_token ? ` (#${busyWith.display_token})` : ''}. Please wait until the current
            consultation is completed.
          </div>
          <div style={{ marginTop:4 }}>
            This patient has <b>not</b> been moved — they remain <b>Ready for Consultation</b>.
          </div>
          {busyWith.priority !== 'Emergency' && priority === 'Emergency' && !confirmEmergency && (
            <button className="btn-primary" style={{ marginTop:10, fontSize:12, padding:'5px 12px' }}
              onClick={()=>setConfirmEmergency(true)}>
              Hold &amp; attend emergency
            </button>
          )}
          {confirmEmergency && (
            <div style={{ marginTop:10 }}>
              <div style={{ marginBottom:8 }}>
                The current consultation will be temporarily placed on hold. You can resume it after completing
                the emergency consultation — nothing already recorded is lost.
              </div>
              <button className="btn-primary" style={{ fontSize:12, padding:'5px 12px', marginRight:8 }}
                disabled={saving} onClick={handleAttendEmergency}>
                {saving ? 'Holding…' : `Hold ${busyWith.patient_name} & continue`}
              </button>
              <button className="btn-ghost" style={{ fontSize:12, padding:'5px 12px' }}
                onClick={()=>setConfirmEmergency(false)}>Cancel</button>
            </div>
          )}
          {busyWith.priority === 'Emergency' && (
            <div style={{ marginTop:4 }}>
              That is an emergency consultation and cannot be interrupted. This patient keeps their place in the queue.
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign:'center', padding:'20px', background:'#F0FDF4', border:'1px solid #A7F3D0', borderRadius:'var(--radius)', marginBottom:20 }}>
        <div style={{ fontSize:12, color:'#065F46', fontWeight:600 }}>Token number</div>
        <div style={{ fontSize: hasToken ? 32 : 52, fontWeight:800, color:'var(--teal)' }}>{hasToken ? `#${localQueueItem!.display_token}` : '—'}</div>
        {hasToken && (
          <div style={{ fontSize:11, color:'#065F46', opacity:.8, marginBottom:4 }}>
            {localQueueItem!.appointment_slot_id ? 'Assigned at check-in' : 'Token issued'}
          </div>
        )}
        {localQueueItem && !hasToken && (
          <div style={{ fontSize:11, color:'#6B7280' }}>Pick a doctor below to issue a token</div>
        )}
        {localQueueItem?.status_token && (
          <a href={queueStatusUrl(localQueueItem.status_token)} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#065F46', fontWeight:600, textDecoration:'underline' }}>
            🔗 Open patient status page
          </a>
        )}
      </div>
      <div style={{ marginBottom:14, position:'relative' }}>
        <label className="lbl">Assign to doctor</label>
        {doctor ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }}>
            <div>
              <div style={{ fontWeight:600, fontSize:13 }}>👨‍⚕️ {doctor.full_name}</div>
              <div style={{ fontSize:11, color:'#6B7280' }}>{doctorStatusLine(onDutyStatus[doctor.id])}</div>
            </div>
            <button className="btn-ghost" style={{ fontSize:11, padding:'3px 8px' }} onClick={()=>{ setDoctor(null); setDoctorQuery(''); }}>Change</button>
          </div>
        ) : (
          <>
            <input value={doctorQuery} onChange={e=>{ setDoctorQuery(e.target.value); setPickerOpen(true); }}
              onFocus={()=>setPickerOpen(true)} placeholder="Search doctors by name…" />
            {pickerOpen && (
              <div style={{ position:'absolute', zIndex:10, top:'100%', left:0, right:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,.08)', marginTop:4, maxHeight:260, overflowY:'auto' }}>
                {matches.length === 0 && <div style={{ padding:'10px 12px', fontSize:12, color:'#9CA3AF' }}>No doctors found</div>}
                {matches.map(d => (
                  <div key={d.id} onClick={()=>handleSelectDoctor(d)}
                    style={{ padding:'8px 12px', cursor:'pointer' }}
                    onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.background='#F9FAFB'} onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.background='transparent'}>
                    <div style={{ fontSize:13 }}>{d.full_name}</div>
                    <div style={{ fontSize:11, color:'#6B7280' }}>{doctorStatusLine(onDutyStatus[d.id])}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div style={{ marginBottom:14 }}>
        <label className="lbl">Priority</label>
        <div style={{ display:'flex', gap:8 }}>
          {(['Normal','Urgent','Emergency'] as const).map(p=>(
            <div key={p} role="button" aria-pressed={priority===p} aria-disabled={!canChangePriority}
              onClick={()=>{ if (canChangePriority) setPriority(p); }}
              className={canChangePriority ? undefined : 'cursor-not-allowed opacity-60'}
              style={{ padding:'6px 16px', borderRadius:20, cursor:'pointer', fontWeight:600, fontSize:13, background:priority===p?(p==='Emergency'?'#FEE2E2':p==='Urgent'?'#FEF3C7':'#D1FAE5'):'#F3F4F6', color:priority===p?(p==='Emergency'?'#991B1B':p==='Urgent'?'#92400E':'#065F46'):'#6B7280', border:'1px solid transparent', transition:'all .15s' }}>{p}</div>
          ))}
        </div>
        {!canChangePriority && (
          <div className="mt-1.5 text-[11.5px] text-ink-muted">
            Your role cannot change a patient&apos;s priority. Ask a doctor or nurse to mark this patient Urgent or Emergency if they need to be seen sooner.
          </div>
        )}
      </div>
      {rules && (rules.rules.referral.enabled || travelManual || customOn.length > 0 || autoLabels.length > 0) && (
        <div className="mb-3.5">
          <label className="lbl">Queue preferences</label>
          <div className="flex flex-col gap-2.5 rounded-[var(--radius)] border border-border p-3">
            {rules.rules.referral.enabled && (
              <div>
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={isReferral} disabled={!canChangePriority}
                    onChange={(e) => setIsReferral(e.target.checked)} />
                  Routine referral from another doctor or facility
                </label>
                {isReferral && (
                  <input className="mt-1.5" value={referralSource} maxLength={120} disabled={!canChangePriority}
                    onChange={(e) => setReferralSource(e.target.value)}
                    placeholder={rules.rules.referral.require_referral_source ? 'Referred by (required for the referral preference)' : 'Referred by (optional)'} />
                )}
                <div className="mt-1 text-[11px] text-ink-muted">An urgent referral is clinical urgency — set the priority to Urgent instead.</div>
              </div>
            )}
            {travelManual && (
              <div>
                <label className="text-[13px]" htmlFor="queue-travel-km">Distance travelled today (km)</label>
                <input id="queue-travel-km" type="number" min={0} max={5000} inputMode="numeric" className="mt-1"
                  value={travelKm} disabled={!canChangePriority} onChange={(e) => setTravelKm(e.target.value)}
                  placeholder={`Preference applies from ${rules.rules.travel.min_distance_km} km`} />
                {travelInvalid && <div className="mt-1 text-[11px] text-danger">Enter a whole number from 0 to 5000.</div>}
              </div>
            )}
            {customOn.map((c) => (
              <div key={c.key}>
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={tags.includes(c.key)} disabled={!canChangePriority}
                    onChange={(e) => toggleTag(c.key, e.target.checked)} />
                  {c.label}
                </label>
                {c.description && <div className="ml-6 mt-0.5 text-[11px] text-ink-muted">{c.description}</div>}
              </div>
            ))}
            {autoLabels.length > 0 && (
              <div className="text-[11.5px] text-ink-muted">
                Applied automatically from the patient record (no tick needed): {autoLabels.join(', ')}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </StageLayout>
    <StageActionBar
      readiness={!localQueueItem ? 'Not yet added to the queue' : '✓ Ready to continue'}
      readinessTone={!localQueueItem ? 'warn' : 'success'}
    >
      {!localQueueItem ? (
        <button className="btn-primary" onClick={handleAdd} disabled={saving||loading}>
          {saving ? 'Adding to queue…' : 'Add to queue'}
        </button>
      ) : (
        <button className="btn-primary" onClick={handleContinue} disabled={saving||loading}>
          {saving ? 'Saving…' : 'Continue →'}
        </button>
      )}
    </StageActionBar>
    <OutOfOrderDialog
      open={!!skip}
      patientName={visit.name || 'this patient'}
      expected={skip?.expected ?? null}
      position={skip?.position}
      pending={loading}
      onCancel={() => setSkip(null)}
      onConfirm={(reason) => {
        if (!skip) return;
        const data = skip.stageData;
        setSkip(null);
        completeStage('queue', data, '', { out_of_order_reason: reason });
      }}
    />
    </div>
  );
}
